import { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'sonner';
import { DChip, Icon, Pane, PaneHead } from '@/components/kit';
import { useShellChrome } from '@/layouts/shellChrome';
import { usePageMeta } from '@/hooks/usePageMeta';
import {
  captureFile, errorText, useTechActions, useTechTask, useWorklist, type Eye, type PriorStudy as Prior, type TechTask,
} from './api';
import { ageSex, dayMonth, drName, eyeText, eyeValue, sendsTo, shortName } from './format';
import { isClosed } from './model';
import { DeviceCard } from './components/DeviceCard';
import { NoteField } from './components/NoteField';
import { PriorStudy } from './components/PriorStudy';
import { sendsToLine } from './components/WorklistPane';
import { PhoneScreen } from './components/PhoneScreen';
import { useTechOverlays } from './components/TechOverlays';

const ACCEPT_FILE = 'application/pdf,image/*,.dcm,application/dicom,.zip';

/** "Sent — Dr. Ahmed has it" / "Done — Dr. Waleed gets the result". */
function sentLine(t: TechTask) {
  return t.testOnly ? `Done — ${sendsTo(t)} gets the result` : `Sent — ${drName(t.requestedBy) || 'the doctor'} has it`;
}

/** "OCT — macula · Dr. Ahmed · urgent, visit open" */
function fulfilsLine(t: TechTask) {
  const tail = [t.priority !== 'routine' ? t.priority : '', t.visitOpen ? 'visit open' : ''].filter(Boolean).join(', ');
  const step = t.bundle ? `step ${t.bundle.index} of ${t.bundle.size} in the bundle` : '';
  return [t.title, drName(t.requestedBy) || (t.testOnly ? sendsTo(t) : ''), step || tail].filter(Boolean).join(' · ');
}

function priorLine(t: TechTask, prior: Prior | null | undefined) {
  if (t.sequence === 'before_dilation') return 'before dilation — order matters';
  if (t.sequence === 'after_drops' && !prior) return 'after drops — the pupil is dilated first';
  if (prior) return `prior ${t.kindLabel} ${dayMonth(prior.date)} — attached for comparison`;
  return `no prior ${t.kindLabel} on file`;
}

/** The capture's own state: the task, the note being typed, the file input. */
function useCapture(id: string | undefined) {
  const navigate = useNavigate();
  const q = useTechTask(id);
  const act = useTechActions();
  const t = q.data;
  const [note, setNoteState] = useState<string | null>(null);
  /* The send and the primary are published into the shell chrome, which only
     re-publishes when its signature changes — typing a note must not have to
     change it, so the send reads the note through a ref. */
  const noteRef = useRef<string | null>(null);
  const setNote = (v: string) => {
    noteRef.current = v;
    setNoteState(v);
  };
  const [busy, setBusy] = useState(false);
  const claimed = useRef(false);

  /* OPENING THE CAPTURE IS STARTING IT — you are at the machine. A test that
     is still waiting, or claimed by somebody else, is not taken. */
  useEffect(() => {
    if (!t || claimed.current) return;
    if (t.state === 'open' && t.lane !== 'waiting' && (!t.assignee || t.mine)) {
      claimed.current = true;
      act.start.mutate(t.id, { onError: (e) => toast.error(errorText(e)) });
    }
  }, [t, act.start]);

  const noteValue = note ?? t?.note ?? '';
  const commitNote = (v: string) => {
    if (!t || isClosed(t) || v === (t.note ?? '')) return;
    act.note.mutate({ id: t.id, note: v }, { onError: (e) => toast.error(errorText(e)) });
  };
  const uploading = useRef(false);
  const upload = async (file: File | undefined) => {
    /* A second pick while one is in flight would attach the file twice. */
    if (!file || !t || uploading.current) return;
    uploading.current = true;
    setBusy(true);
    try {
      await captureFile(t.id, file);
      toast.success(`${file.name} attached`);
      await q.refetch();
    } catch (e) {
      toast.error(errorText(e));
    } finally {
      uploading.current = false;
      setBusy(false);
    }
  };
  const send = () => {
    if (!t) return;
    act.send.mutate({ id: t.id, note: (noteRef.current ?? t.note ?? '').trim() }, {
      onSuccess: () => {
        toast.success(sentLine(t));
        navigate(t.bundle && t.bundle.index < t.bundle.size ? `/technician/bundle/${t.id}` : '/technician/worklist');
      },
      onError: (e) => toast.error(errorText(e)),
    });
  };
  return { q, t, act, noteValue, setNote, commitNote, upload, busy, send };
}

type Cap = ReturnType<typeof useCapture>;

/**
 * Technician · Capture — `g_cap`, `g_down`, `g_capr`; phone `g_t2`.
 *
 * One capture surface, two endings, and the order decides which. A VALUES
 * test (refraction) is the same surface with a values form where the drop zone
 * would be — never docked beside another test.
 */
export function TechCapturePage() {
  const { id } = useParams();
  const navigate = useNavigate();
  const cap = useCapture(id);
  const { t, q } = cap;
  const list = useWorklist();
  const [tab, setTab] = useState<'order' | 'prior'>('order');
  const fileRef = useRef<HTMLInputElement>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  const [over, setOver] = useState(false);
  usePageMeta({ title: t ? t.kindLabel : 'Capture' });
  const ov = useTechOverlays();

  const down = t?.device?.status === 'down';
  const closed = t ? isClosed(t) : false;
  const takenByOther = Boolean(t && t.assignee && !t.mine && !closed);
  const siblings = (list.data?.tasks ?? [])
    .filter((x) => t?.bundle && x.bundle?.key === t.bundle.key)
    .sort((a, b) => (a.bundle?.index ?? 0) - (b.bundle?.index ?? 0));
  const handOver = () => navigate('/technician/worklist/hand-over', { state: { select: t ? [t.id] : [] } });

  const sub = !t ? '' : t.valuesForm && t.bundle
    ? `${shortName(t.patient)} · bundle — step ${t.bundle.index} of ${t.bundle.size}`
    : down ? `${t.kindLabel} · the device is down`
      : closed ? `${t.kindLabel} · ${t.state === 'done' ? 'sent' : 'came back'}`
        : [t.kindLabel, [t.priority !== 'routine' ? t.priority : '', t.visitOpen ? 'the visit is open' : t.testOnly ? 'test-only' : ''].filter(Boolean).join(' — ')]
          .filter(Boolean).join(' · ');

  const primary = !t || closed || takenByOther ? undefined
    : down && !t.valuesForm ? { label: 'Hand to', onClick: handOver }
      : { label: t.valuesForm ? `Send — ${sendsTo(t)}` : `Attach & send — ${sendsTo(t)}`, onClick: cap.send };

  const sig = t
    ? [t.id, t.state, t.device?.status, t.captures.length, t.note, JSON.stringify(t.values), tab, siblings.map((s) => s.state).join(','), q.data?.prior?.attachments.length ?? -1].join('|')
    : 'loading';

  useShellChrome(
    {
      raw: true,
      title: t?.kindLabel ?? '',
      sub: t ? (
        <>
          <button type="button" className="lnk" style={{ fontWeight: 600 }} onClick={() => navigate('/technician/worklist')}>‹ Worklist</button>
          {' ▸ '}{sub}
        </>
      ) : undefined,
      search: 'Search orders, patients, MRN…',
      onSearch: ov.openSearch,
      onBell: ov.openBell,
      primary,
      pane: t ? (
        <Pane
          head={(
            <PaneHead
              overline="This order"
              title={t.title}
              sub={t.valuesForm
                ? [shortName(t.patient), drName(t.requestedBy), t.takesMin ? `≈${t.takesMin} min` : ''].filter(Boolean).join(' · ')
                : [eyeText(t.laterality), drName(t.requestedBy) || sendsTo(t), t.priority !== 'routine' ? t.priority : ''].filter(Boolean).join(' · ')}
            />
          )}
          foot={closed ? null : t.valuesForm ? (
            <>
              <button type="button" className="btn btn-warnline grow" onClick={() => navigate(`/technician/worklist/${t.id}/cant`)}>Can’t — reason ▾</button>
              <button type="button" className="btn btn-outline grow" onClick={handOver}>Hand to…</button>
            </>
          ) : (
            <button type="button" className="btn btn-warnline grow" onClick={() => navigate(`/technician/worklist/${t.id}/cant`)}>Can’t — reason ▾</button>
          )}
        >
          {t.valuesForm ? null : (
            <div className="tab2" style={{ marginBottom: 12 }} role="tablist">
              <button type="button" role="tab" aria-selected={tab === 'order'} className={`t${tab === 'order' ? ' on' : ''}`} onClick={() => setTab('order')}>This order</button>
              <button type="button" role="tab" aria-selected={tab === 'prior'} className={`t${tab === 'prior' ? ' on' : ''}`} onClick={() => setTab('prior')}>Prior study</button>
            </div>
          )}
          {tab === 'prior' && !t.valuesForm ? (
            <PriorStudy prior={q.data?.prior} kindLabel={t.kindLabel} />
          ) : (
            <>
              <div className="card">
                <p className="t-ovl mut">The doctor asked for</p>
                <p className="t-strong mt8">{t.title}{t.laterality ? `, ${eyeText(t.laterality)}` : ''}</p>
                {t.indication || t.priority !== 'routine' ? (
                  <p className="t-sec sec mt8">
                    {[t.indication ? `why: ${t.indication}` : '', [t.priority !== 'routine' ? t.priority : '', t.visitOpen ? 'visit open' : ''].filter(Boolean).join(' — ')]
                      .filter(Boolean).join(' · ')}
                  </p>
                ) : null}
              </div>
              {t.valuesForm && siblings.length > 1 ? (
                <div className="card">
                  <p className="t-ovl mut">The sequence</p>
                  {siblings.map((s, i) => (
                    <p key={s.id} className={`t-sec mt8${s.id === t.id ? '' : ' mut'}`}>
                      {s.id === t.id ? <><span className="w6">{i + 1} · {s.title}</span> — this one</> : (
                        <>{i + 1} · {s.title}{s.state === 'done' ? ' — done ✓' : s.sequence === 'before_dilation' ? ' — before dilation' : s.sequence === 'after_drops' ? ' — after drops' : ''}</>
                      )}
                    </p>
                  ))}
                </div>
              ) : null}
              {closed ? null : <DeviceCard task={t} />}
              <div className="card card-acc">
                <p className="t-sec" style={{ color: 'var(--brand-600)' }}>{sendsToLine(t)}</p>
              </div>
              {t.valuesForm ? (
                <div className="card card-flat">
                  <p className="t-sec sec">Three exits, like every task: capture it, hand it over, or say you can’t — a values form is not an exception.</p>
                </div>
              ) : null}
            </>
          )}
        </Pane>
      ) : null,
      phone: <PhoneCapture cap={cap} />,
    },
    [sig],
  );

  if (!t) {
    return (
      <div className="maie desk tech-root">
        {ov.overlays}
        <div className="dcontent">
          {q.isError ? (
            <div className="emptystate">
              <p className="es-t">This test is not on your worklist.</p>
              <p className="mt12"><DChip kind="outline" onClick={() => navigate('/technician/worklist')}>Back to the worklist</DChip></p>
            </div>
          ) : null}
        </div>
      </div>
    );
  }

  return (
    <div className="maie desk tech-root">
        {ov.overlays}
      <div className="dcontent">
        <IdentityStrip t={t} onMismatch={() => navigate('/technician/worklist')} />
        <div className="card card-acc">
          <p className="t-sec" style={{ color: 'var(--brand-600)' }}><span className="w6">Fulfills:</span> {fulfilsLine(t)}</p>
          <p className="t-sec mut mt8">{priorLine(t, q.data?.prior)}</p>
        </div>
        {takenByOther ? (
          <div className="card card-warn">
            <p className="t-sec">{shortName(t.assignee)} has claimed this test — nobody else can start it.</p>
          </div>
        ) : null}
        {t.repeatReason ? (
          <div className="card" style={{ borderColor: 'var(--danger-600)' }}>
            <p className="t-sec red">{drName(t.requestedBy) || 'The doctor'}: “{t.repeatReason}”</p>
          </div>
        ) : null}

        {t.valuesForm ? (
          <ValuesForm t={t} cap={cap} />
        ) : (
          <>
            {down && !closed ? <DownCard t={t} onHandOver={handOver} /> : null}
            {!down && !closed && !takenByOther ? (
              <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
                <button
                  type="button"
                  className="card card-ghost tech-drop"
                  data-over={over}
                  style={{ flex: 1, margin: 0, textAlign: 'center', borderColor: 'var(--brand-300)', cursor: 'pointer', font: 'inherit' }}
                  onClick={() => fileRef.current?.click()}
                  onDragOver={(e) => {
                    e.preventDefault();
                    setOver(true);
                  }}
                  onDragLeave={() => setOver(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setOver(false);
                    void cap.upload(e.dataTransfer.files?.[0]);
                  }}
                  disabled={cap.busy}
                >
                  <p className="t-strong brand"><Icon name="file-upload" /> {cap.busy ? 'Attaching…' : 'Attach the machine’s file'}</p>
                  <p className="t-sec mut mt8">PDF · image · DICOM export — or drop it here</p>
                </button>
                <button
                  type="button"
                  className="card card-ghost"
                  style={{ flex: 1, margin: 0, textAlign: 'center', borderColor: 'var(--brand-300)', cursor: 'pointer', font: 'inherit' }}
                  onClick={() => scanRef.current?.click()}
                  disabled={cap.busy}
                >
                  <p className="t-strong brand"><Icon name="camera" /> Scan the printout</p>
                  <p className="t-sec mut mt8">flatbed or phone</p>
                </button>
              </div>
            ) : null}
            <AttachedTable t={t} cap={cap} />
            <NoteCard t={t} cap={cap} hint="a bad scan and the reason for it are different facts — yours travels with the study, attributed." />
            <div className="card card-flat">
              <p className="t-ovl mut">What the send closes with</p>
              {t.testOnly ? (
                <p className="t-sec sec mt8">
                  This one is <span className="w6">test-only</span> — a walk-in on an outside request, no visit — so the send reads{' '}
                  <span className="w6">“{sentLine(t)}”</span>, and the patient can go.
                </p>
              ) : (
                <p className="t-sec sec mt8">
                  This one has {t.visitOpen ? 'an open visit' : 'a visit'}, so the send reads <span className="w6">“{sentLine(t)}”</span> and the
                  study lands on {t.requestedBy ? `${drName(t.requestedBy)}’s` : 'the doctor’s'} rail.
                </p>
              )}
            </div>
            <p className="t-sec mut mt12">
              {closed && t.completedBy ? `performed by: ${shortName(t.completedBy)} — auto · rides the bill` : 'performed by: you — auto · rides the bill'}
            </p>
          </>
        )}
      </div>
      <input ref={fileRef} type="file" hidden accept={ACCEPT_FILE} onChange={(e) => {
        void cap.upload(e.target.files?.[0]);
        e.target.value = '';
      }} />
      <input ref={scanRef} type="file" hidden accept="image/*" capture="environment" onChange={(e) => {
        void cap.upload(e.target.files?.[0]);
        e.target.value = '';
      }} />
    </div>
  );
}

function IdentityStrip({ t, onMismatch, compact = false }: { t: TechTask; onMismatch: () => void; compact?: boolean }) {
  const act = useTechActions();
  const [confirm, setConfirm] = useState(false);
  const who = [shortName(t.patient), ageSex(t.patient), t.patient?.mrn ? `MRN ${t.patient.mrn}` : t.patient?.newPatient ? 'new patient' : '']
    .filter(Boolean).join(' · ');
  const mismatch = () => act.notThisPatient.mutate(t.id, {
    onSuccess: () => {
      toast.success('Stopped — nothing captured. The test is back on the list.');
      onMismatch();
    },
    onError: (e) => toast.error(errorText(e)),
  });
  const closed = isClosed(t);
  const button = closed ? null : (
    <DChip kind="outline" onClick={() => setConfirm(true)}>✕ Not this patient</DChip>
  );
  return (
    <>
      <div className="card card-flat" style={{ background: 'var(--info-100)' }}>
        <div className="flex" style={{ gap: 12 }}>
          <p className="t-strong grow">{who}</p>
          {compact ? null : button}
        </div>
        {compact ? null : (
          <p className="t-sec sec mt8">
            {t.valuesForm
              ? 'the identity strip — the same guard as every capture, and it names the patient it is guarding'
              : 'the identity strip — checked against the person in the chair before anything is captured'}
          </p>
        )}
      </div>
      {compact && button ? <p className="center mb12">{button}</p> : null}
      {confirm ? (
        <ConfirmMismatch t={t} onCancel={() => setConfirm(false)} onConfirm={() => {
          setConfirm(false);
          mismatch();
        }} />
      ) : null}
    </>
  );
}

function ConfirmMismatch({ t, onCancel, onConfirm }: { t: TechTask; onCancel: () => void; onConfirm: () => void }) {
  return (
    <div className="card" style={{ borderColor: 'var(--danger-600)', background: 'var(--danger-100)' }}>
      <p className="t-strong red">The person in the chair is not {shortName(t.patient) || 'this patient'}?</p>
      <p className="t-sec sec mt8">Nothing is captured. Your claim is released and the test goes back on the list, with the mismatch noted on it.</p>
      <p className="mt8" style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
        <DChip kind="danger" onClick={onConfirm}>Stop — not {shortName(t.patient) || 'this patient'}</DChip>
        <DChip kind="outline" onClick={onCancel}>It is {shortName(t.patient) || 'them'}</DChip>
      </p>
    </div>
  );
}

function DownCard({ t, onHandOver }: { t: TechTask; onHandOver: () => void }) {
  const act = useTechActions();
  const d = t.device!;
  return (
    <div className="card" style={{ borderColor: 'var(--danger-600)', background: 'var(--danger-100)' }}>
      <p className="t-strong red">{[d.name, d.room].filter(Boolean).join(' · ')} is marked down</p>
      <p className="t-sec sec mt8">
        Nothing can be captured here. Every pending and future {t.kindLabel} re-routes: the desk raises the external request, or
        the order is handed to a colleague whose machine is up.
      </p>
      <p className="mt8">
        <DChip kind="warn" onClick={() => act.raiseExternal.mutate(t.id, {
          onSuccess: () => toast.success('The desk is raising the outside request'),
          onError: (e) => toast.error(errorText(e)),
        })}>
          {t.externalRaised ? 'The desk was told ✓' : 'Tell the desk to raise it'}
        </DChip>{' '}
        <DChip kind="outline" onClick={onHandOver}>Hand the order over</DChip>
      </p>
    </div>
  );
}

function AttachedTable({ t, cap }: { t: TechTask; cap: Cap }) {
  const closed = isClosed(t);
  return (
    <table className="dtable">
      <thead>
        <tr>
          <th style={{ width: '46%' }}>Attached</th>
          <th style={{ width: '30%' }}>Quality</th>
          <th style={{ width: '24%' }} />
        </tr>
      </thead>
      <tbody>
        {t.captures.length === 0 ? (
          <tr className="rowc">
            <td colSpan={3}><span className="t-sec mut">Nothing attached yet — the send carries the machine’s file.</span></td>
          </tr>
        ) : t.captures.map((c) => (
          <tr key={c.id} className="rowc">
            <td><span className="t-body w6">{c.name ?? 'file'} ✓</span></td>
            <td><span className="t-sec mut">reviewed by you</span></td>
            <td>
              {closed ? null : (
                <DChip kind="outline" onClick={() => cap.act.removeCapture.mutate({ id: t.id, attachmentId: c.id }, {
                  onSuccess: () => toast.success('Taken off — attach the retake'),
                  onError: (e) => toast.error(errorText(e)),
                })}>
                  ↻ Retake
                </DChip>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function NoteCard({ t, cap, hint, compact = false }: { t: TechTask; cap: Cap; hint?: string; compact?: boolean }) {
  const closed = isClosed(t);
  return (
    <div className="card card-ghost" style={{ marginTop: compact ? 0 : 12 }}>
      <p className="t-ovl mut">My note <span style={{ textTransform: 'none', letterSpacing: 0 }}>(optional)</span></p>
      <p className={`t-sec sec ${compact ? 'mt4' : 'mt8'}`}>
        {closed ? (t.note ? `“${t.note}”` : '—') : (
          <NoteField label="My note" value={cap.noteValue} onChange={cap.setNote} onCommit={cap.commitNote}
            placeholder="what the doctor should know about this capture" maxLength={1000} />
        )}
      </p>
      {hint && !compact ? <p className="t-sec sec mt8">{hint}</p> : null}
    </div>
  );
}

const FIELDS: Array<{ key: keyof Eye; label: string }> = [
  { key: 'sphere', label: 'Sphere' },
  { key: 'cylinder', label: 'Cylinder' },
  { key: 'axis', label: 'Axis' },
];

function parseValue(field: keyof Eye, raw: string): number | null | 'bad' {
  const s = raw.trim().replace(/[−–]/g, '-').replace('°', '');
  if (!s || s === '-' || s === '—') return null;
  const n = Number(s);
  if (Number.isNaN(n)) return 'bad';
  if (field === 'axis') return n >= 0 && n <= 180 ? Math.round(n) : 'bad';
  return Math.abs(n) <= 30 ? Math.round(n * 4) / 4 : 'bad';
}

/** THE VALUES — typed at the machine, OD and OS, saved as each one is left. */
function ValuesForm({ t, cap, phone = false }: { t: TechTask; cap: Cap; phone?: boolean }) {
  const closed = isClosed(t);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const values = t.values ?? {};
  const save = (eye: 'od' | 'os', field: keyof Eye, raw: string) => {
    const v = parseValue(field, raw);
    if (v === 'bad') {
      toast.error(field === 'axis' ? 'Axis is 0–180°' : 'Type a dioptre value, e.g. −2.25');
      return;
    }
    const next = { ...(values[eye] ?? {}), [field]: v };
    cap.act.values.mutate({ id: t.id, [eye]: next }, {
      onSuccess: () => setDraft((d) => {
        const { [`${eye}.${field}`]: _gone, ...rest } = d;
        void _gone;
        return rest;
      }),
      onError: (e) => toast.error(errorText(e)),
    });
  };
  const column = (eye: 'od' | 'os', heading: string) => (
    <div style={{ flex: 1, minWidth: 0 }}>
      <p className="t-heading mb8">{heading}</p>
      {FIELDS.map((f) => {
        const k = `${eye}.${f.key}`;
        const stored = eyeValue(f.key, values[eye]?.[f.key]);
        return (
          <label key={f.key} className="card" style={{ marginBottom: 8, cursor: closed ? 'default' : 'text', display: 'block' }}>
            <span className="t-ovl mut" style={{ display: 'block' }}>{f.label}</span>
            <span className="t-strong mt8" style={{ display: 'flex', alignItems: 'center', gap: 4 }}>
              {closed ? stored : (
                <>
                  <input
                    className="tech-val"
                    aria-label={`${heading} ${f.label}`}
                    inputMode="decimal"
                    value={draft[k] ?? (stored === '—' ? '' : stored)}
                    /* As wide as what is typed, so the pencil follows the value. */
                    style={{ width: `${Math.max(2, (draft[k] ?? (stored === '—' ? '—' : stored)).length + 0.5)}ch` }}
                    placeholder="—"
                    onChange={(e) => setDraft((d) => ({ ...d, [k]: e.target.value }))}
                    onBlur={(e) => {
                      if (draft[k] !== undefined) save(eye, f.key, e.target.value);
                    }}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') (e.target as HTMLInputElement).blur();
                    }}
                  />
                  <Icon name="pencil" size="sm" className="mut" />
                </>
              )}
            </span>
          </label>
        );
      })}
    </div>
  );
  return (
    <>
      <p className="t-ovl mut mt16 mb8">The values — typed at the machine</p>
      <div style={{ display: 'flex', gap: phone ? 10 : 20, alignItems: 'flex-start' }}>
        {column('od', 'OD — right')}
        {column('os', 'OS — left')}
      </div>
      <NoteCard t={t} cap={cap} />
      <p className="t-sec mut mt12">
        {closed && t.completedBy ? `performed by: ${shortName(t.completedBy)} — auto · rides the bill` : 'performed by: you — auto · rides the bill'}
      </p>
      {phone ? null : <p className="t-sec sec mt8">a values form belongs on the capture it belongs to — never docked beside another test.</p>}
    </>
  );
}

/* ─────────────────────────── phone · g_t2 ─────────────────────────── */

function PhoneCapture({ cap }: { cap: Cap }) {
  const navigate = useNavigate();
  const { t, q } = cap;
  const fileRef = useRef<HTMLInputElement>(null);
  const scanRef = useRef<HTMLInputElement>(null);
  if (!t) {
    return (
      <PhoneScreen door="worklist" crumb="Worklist" title="" onBack={() => navigate('/technician/worklist')}>
        {q.isError ? <div className="card card-ghost center"><p className="t-sec mut">This test is not on your worklist.</p></div> : null}
      </PhoneScreen>
    );
  }
  const closed = isClosed(t);
  const down = t.device?.status === 'down';
  const handOver = () => navigate('/technician/worklist/hand-over', { state: { select: [t.id] } });
  return (
    <PhoneScreen door="worklist" crumb="Worklist" title={t.valuesForm && t.bundle ? `${shortName(t.patient)} · ${t.title}` : t.kindLabel}
      onBack={() => navigate(t.bundle ? `/technician/bundle/${t.id}` : '/technician/worklist')}>
      <IdentityStrip t={t} compact onMismatch={() => navigate('/technician/worklist')} />
      <div className="card card-acc">
        <p className="t-sec" style={{ color: 'var(--brand-600)' }}><span className="w6">Fulfills:</span> {fulfilsLine(t)}</p>
        <p className="t-cap mut mt4">{priorLine(t, q.data?.prior)}</p>
      </div>
      {t.valuesForm ? <ValuesForm t={t} cap={cap} phone /> : (
        <>
          {down && !closed ? <DownCard t={t} onHandOver={handOver} /> : null}
          {!down && !closed ? (
            <>
              <button type="button" className="row w-full text-start" style={{ borderRadius: 999 }} onClick={() => scanRef.current?.click()} disabled={cap.busy}>
                <span className="brand"><Icon name="camera" /></span>
                <p className="t-sec sec grow">Scan the printout</p>
              </button>
              <button type="button" className="row w-full text-start" style={{ borderRadius: 999 }} onClick={() => fileRef.current?.click()} disabled={cap.busy}>
                <span className="brand"><Icon name="file-upload" /></span>
                <p className="t-sec sec grow">{cap.busy ? 'Attaching…' : 'Attach the machine’s file — PDF, image, DICOM export'}</p>
              </button>
            </>
          ) : null}
          {t.captures.map((c) => (
            <div key={c.id} className="card" style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
              <div style={{
                width: 52, height: 40, background: 'var(--info-100)', border: '1px solid var(--stroke-200)', borderRadius: 6,
                display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--ink-400)', flexShrink: 0,
              }}>
                <Icon name="photo" />
              </div>
              <div className="grow">
                <p className="t-sec w6">{c.name ?? 'file'} ✓</p>
                {closed ? null : (
                  <p className="t-cap mut">quality ok?{' '}
                    <button type="button" className="lnk w6 brand" onClick={() => cap.act.removeCapture.mutate({ id: t.id, attachmentId: c.id }, {
                      onError: (e) => toast.error(errorText(e)),
                    })}>↻ Retake</button>
                  </p>
                )}
              </div>
            </div>
          ))}
          <NoteCard t={t} cap={cap} compact />
        </>
      )}
      {closed ? (
        <p className="t-sec mut center mt16">{t.state === 'done' ? sentLine(t) : `Came back — ${t.cantNote ?? 'with a reason'}`}</p>
      ) : down && !t.valuesForm ? (
        <button type="button" className="btn btn-primary mt16" onClick={handOver}>Hand to</button>
      ) : (
        <button type="button" className="btn btn-primary mt16" onClick={cap.send} disabled={cap.act.send.isPending}>
          {t.valuesForm ? `Send — ${sendsTo(t)}` : `Attach & send — ${sendsTo(t)}${t.visitOpen ? ', waiting' : ''}`}
        </button>
      )}
      {!closed ? (
        <p className="center mt12">
          <button type="button" className="btn btn-warnline" onClick={() => navigate(`/technician/worklist/${t.id}/cant`)}>Can’t — reason ▾</button>
        </p>
      ) : null}
      {!t.valuesForm ? (
        <p className="t-sec mut center mt12">
          {t.testOnly ? `test-only patient → “${sentLine(t)}”` : `test-only patient? → “Done — the referrer gets the result”`}
        </p>
      ) : null}
      <p className="t-sec mut center mt8">performed by: you — auto · rides the bill</p>
      <input ref={fileRef} type="file" hidden accept={ACCEPT_FILE} onChange={(e) => {
        void cap.upload(e.target.files?.[0]);
        e.target.value = '';
      }} />
      <input ref={scanRef} type="file" hidden accept="image/*" capture="environment" onChange={(e) => {
        void cap.upload(e.target.files?.[0]);
        e.target.value = '';
      }} />
    </PhoneScreen>
  );
}
